/**
 * Set-property-amenities request validator — SCRUM-140 / DS-010-4.
 *
 * HTTP-boundary Zod schema for the full-replace PUT payload that assigns a
 * property's amenity set (`PUT /organizations/:slug/properties/:propertyId/amenities`).
 *
 * Composed from the amenity policy (`AMENITY_NOTE_MAX_LENGTH`) reused from
 * `../policies/amenity.js` — the same constant backs the property domain
 * `AmenityNote` value object (SCRUM-138) and the `char_length(note) <= 500`
 * CHECK on `property.property_amenity`, so the Zod schema, the VO, and the
 * DB CHECK share one source of truth. Do NOT hardcode the cap here.
 *
 * Note semantics at the wire boundary:
 *   - `note` is RAW free text — it is NOT trimmed at this layer. Trailing
 *     whitespace / newlines / CRLF are preserved as legitimate content (the
 *     domain `AmenityNote` VO trims separately). This differs from
 *     `amenityLabelSchema`, which adds a trimmed non-empty constraint.
 *   - The cap is applied to the RAW Unicode codepoint count (`[...v].length`),
 *     matching the `AmenityNote` VO's length semantics and the
 *     `char_length()` DB CHECK — a multi-codepoint emoji counts as 1, NOT 2
 *     (which `.max()` on UTF-16 code units would mis-count).
 *   - `note` is `.nullable().optional()`: omitted (no note) and explicit
 *     `null` (clear the note) are both accepted; an empty string is allowed
 *     free text (no `.min()` constraint).
 *
 * `items: []` is a VALID payload — it is the clear-all operation. A missing
 * `items` key is invalid (the field is required).
 */
import { z } from 'zod';
import { AMENITY_NOTE_MAX_LENGTH } from '../policies/amenity.js';
/**
 * Strict RFC-4122 UUID regex (lowercase hex; version nibble pinned to 1–5;
 * variant nibble pinned to 8/9/a/b).
 *
 * Intentionally NOT `z.string().uuid()` — Zod v3's built-in validator is
 * lenient on the version/variant nibbles and would accept a structurally
 * malformed id such as `…-9ccc-…` (version 9 does not exist). The amenity
 * id is a foreign key into `property.amenity_catalog`; rejecting a malformed
 * version digit at the HTTP boundary yields a clean 422 instead of a later
 * `InvalidAmenityIdsError` 422 from the use case. Lowercase-only mirrors the
 * `add-logo.schema.ts` LOWERCASE_UUID precedent (domain ids are lowercase).
 */
const STRICT_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
export const setPropertyAmenitiesItemSchema = z.object({
    amenityId: z.string().regex(STRICT_UUID),
    note: z
        .string()
        .refine((v) => [...v].length <= AMENITY_NOTE_MAX_LENGTH, {
        message: `Note must be at most ${AMENITY_NOTE_MAX_LENGTH} characters`,
    })
        .nullable()
        .optional(),
});
export const setPropertyAmenitiesSchema = z.object({
    items: z.array(setPropertyAmenitiesItemSchema),
});
//# sourceMappingURL=set-property-amenities.schema.js.map